/**
 * Facility-aware blackout scope for the calendar views.
 *
 * The calendar used to flatten every `venue_blackout` into a bare list of dates
 * — `params.dates` was read and the facility/venue scope was thrown away. So a
 * blackout scoped to ONE facility blacked out the WHOLE day: the views hid every
 * game on it and drag-and-drop was disabled. On Chilliwack flight 164 that meant
 * Jan 3 rendered "BLACKOUT DAY" with nothing on it, while three real games sat on
 * Secondary (Primary was the only rink blacked out) — visible in the table view
 * the whole time.
 *
 * The scheduler has always got this right; only the calendar lied about it. These
 * helpers give the views the same model the generator uses.
 *
 * ── The scope rules MIRROR scheduler-python ───────────────────────────────
 * (`app/api/v1/schedule.py`, the blackout collector — and api2's
 * `SchedulerBlackoutGuard`, which now refuses an edit onto blacked-out ice):
 *
 *     if facility scope:  -> ONLY those facilities are out
 *     elif venue scope:   -> every facility of those venues
 *     else:               -> the whole day, everywhere (global)
 *
 * Facility scope WINS — naming a facility never widens to its venue.
 *
 * Two rules that are easy to get wrong, and would make the calendar disagree with
 * the schedule it is drawing:
 *
 * - **Only HARD blackouts remove ice.** A soft one is a preference; the generator
 *   keeps its slots in the pool (there is a soft blackout row in production).
 * - **Both parameter shapes exist.** The DB stores PLURAL `facility_uids` /
 *   `venue_uids`; `SchedulerConstraintSerializer::canonicalize()` expands them to
 *   SINGULAR `facility_uid` for the generation payload. Accept either, exactly as
 *   python's `effective_facility_uids` does.
 */

import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'
import type { Venue } from '../hooks/useSchedulerLocations'

/** What is unavailable on one calendar date. */
export interface DayBlackout {
  /** Facilities explicitly out on this date. Empty when `isGlobal`. */
  facilityIds: Set<string>
  /** A scope-less blackout: the whole day is out, everywhere. */
  isGlobal: boolean
}

/** `YYYY-MM-DD` → what is unavailable that day. */
export type BlackoutModel = Map<string, DayBlackout>

/**
 * Every facility the flight can actually play on, with its display name.
 * Derived from the venues (not the time slots) so a facility with no template
 * still counts as one that a venue-scoped blackout takes out.
 */
export function facilityNamesById(venues: Venue[]): Map<string, string> {
  const out = new Map<string, string>()
  for (const venue of venues) {
    for (const facility of venue.facilities ?? []) {
      out.set(facility.id, facility.name)
    }
  }
  return out
}

/** Read a scope list from either the plural or the singular parameter shape. */
function scopeIds(
  params: Record<string, unknown>,
  singularKey: string,
  pluralKey: string
): string[] {
  const plural = params[pluralKey]
  if (Array.isArray(plural)) {
    return plural.filter((v): v is string => typeof v === 'string')
  }
  const singular = params[singularKey]
  return typeof singular === 'string' && singular !== '' ? [singular] : []
}

/**
 * Build the date → blackout-scope model from the flight's live constraints.
 *
 * `venues` resolves a venue-scoped blackout down to the facilities it takes out,
 * so every entry in the model is expressed in facilities — the unit the calendar
 * actually renders.
 */
export function buildBlackoutModel(
  constraints: SchedulerConstraint[],
  venues: Venue[]
): BlackoutModel {
  const facilitiesByVenue = new Map<string, string[]>()
  for (const venue of venues) {
    facilitiesByVenue.set(
      venue.id,
      (venue.facilities ?? []).map((f) => f.id)
    )
  }

  const model: BlackoutModel = new Map()
  const dayFor = (dateKey: string): DayBlackout => {
    let day = model.get(dateKey)
    if (!day) {
      day = { facilityIds: new Set<string>(), isGlobal: false }
      model.set(dateKey, day)
    }
    return day
  }

  for (const c of constraints) {
    if (c.type.code !== 'venue_blackout' || !c.is_enabled) {
      continue
    }
    // A SOFT blackout does not remove ice — the generator still fills those
    // slots, so the calendar must not black them out either.
    if (!c.is_hard) {
      continue
    }

    const params = (c.parameters ?? {}) as Record<string, unknown>
    const dates = Array.isArray(params.dates)
      ? params.dates.filter((d): d is string => typeof d === 'string')
      : []
    if (dates.length === 0) {
      continue
    }

    const facilityIds = scopeIds(params, 'facility_uid', 'facility_uids')
    const venueIds = scopeIds(params, 'venue_uid', 'venue_uids')

    for (const dateKey of dates) {
      const day = dayFor(dateKey)
      if (facilityIds.length > 0) {
        // Facility scope WINS — never widen to the venue.
        for (const id of facilityIds) {
          day.facilityIds.add(id)
        }
      } else if (venueIds.length > 0) {
        for (const venueId of venueIds) {
          for (const id of facilitiesByVenue.get(venueId) ?? []) {
            day.facilityIds.add(id)
          }
        }
      } else {
        day.isGlobal = true
      }
    }
  }

  return model
}

/** Facilities out on `dateKey` (empty set when nothing is). */
export function blackedOutFacilityIds(
  model: BlackoutModel,
  dateKey: string
): Set<string> {
  return model.get(dateKey)?.facilityIds ?? new Set<string>()
}

/**
 * Is the ENTIRE day unplayable?
 *
 * Only then may the calendar render "BLACKOUT DAY", hide the games, and refuse a
 * drop. True when the blackout is global, or when every facility the flight can
 * use is out. Anything else is a PARTIAL blackout: real ice remains, real games
 * can (and do) sit on it.
 *
 * `allFacilityIds` empty ⇒ we cannot tell what "every facility" means, so only a
 * global blackout counts — never guess a day fully dark.
 */
export function isFullDayBlackout(
  model: BlackoutModel,
  dateKey: string,
  allFacilityIds: string[]
): boolean {
  const day = model.get(dateKey)
  if (!day) {
    return false
  }
  if (day.isGlobal) {
    return true
  }
  if (allFacilityIds.length === 0) {
    return false
  }
  return allFacilityIds.every((id) => day.facilityIds.has(id))
}

/** Some ice is out, but not all of it — games still belong on this day. */
export function isPartialBlackout(
  model: BlackoutModel,
  dateKey: string,
  allFacilityIds: string[]
): boolean {
  const day = model.get(dateKey)
  if (!day || day.isGlobal) {
    return false
  }
  return (
    day.facilityIds.size > 0 &&
    !isFullDayBlackout(model, dateKey, allFacilityIds)
  )
}

/**
 * The chip copy for a partial day, e.g. `PRIMARY UNAVAILABLE` or
 * `2 RINKS UNAVAILABLE`. Returns null when the day is not partially blacked out.
 */
export function partialBlackoutLabel(
  model: BlackoutModel,
  dateKey: string,
  allFacilityIds: string[],
  facilityNames: Map<string, string>
): string | null {
  if (!isPartialBlackout(model, dateKey, allFacilityIds)) {
    return null
  }
  const out = [...blackedOutFacilityIds(model, dateKey)]
    // Only name facilities the flight actually uses — a blackout may reference
    // a rink this flight never plays on.
    .filter((id) => allFacilityIds.includes(id))
    .map((id) => facilityNames.get(id))
    .filter((name): name is string => Boolean(name))

  if (out.length === 0) {
    return null
  }
  if (out.length === 1) {
    return `${out[0]} unavailable`
  }
  return `${out.length} facilities unavailable`
}
